/**
 * Same shape as every other feature's local `errorMessage()` (see employees/AddEmployeeDialog.tsx)
 * — pulled out once here since the recruitment module has many more mutation call sites than
 * employees did.
 *
 * A REQUEST-BODY validation failure (the whole body failed the contract's Zod schema, e.g. a
 * malformed phone number or URL) DOES carry field detail: `apps/api/src/lib/ts-rest.ts` throws
 * `new ValidationError(\`Invalid ${where}\`, result.error.flatten())`, and `AppError.toBody()`
 * includes `details` on any exposed (4xx) error — so the wire body is
 * `{ code: 'VALIDATION_FAILED', message: 'Invalid body', details: { fieldErrors: { phone: [...] } } }`.
 * Showing only `message` here means every one of this module's many forms displays the same
 * unhelpful "Invalid body" for a mistake the server already described precisely. A route-HANDLER
 * validation failure (e.g. "Insufficient leave balance") carries a different, non-Zod `details`
 * shape or none at all — `fieldErrors` is checked for structurally rather than assumed.
 */
export function errorMessage(error: unknown): string {
  if (error instanceof Error) {
    return "Can't reach the server. Check your connection and try again.";
  }
  const body = (error as { body?: unknown } | undefined)?.body;
  if (!body || typeof body !== 'object') {
    return 'Something went wrong. Please try again.';
  }

  const fieldErrors = fieldErrorsOf(body);
  if (fieldErrors) {
    const parts = Object.entries(fieldErrors)
      .filter(
        (entry): entry is [string, string[]] => Array.isArray(entry[1]) && entry[1].length > 0,
      )
      .map(([field, messages]) => `${field}: ${messages[0]}`);
    if (parts.length > 0) return parts.join('; ');
  }

  if ('message' in body && typeof body.message === 'string') {
    return body.message;
  }
  return 'Something went wrong. Please try again.';
}

function fieldErrorsOf(body: object): Record<string, unknown> | null {
  const details = (body as { details?: unknown }).details;
  if (!details || typeof details !== 'object') return null;
  const fieldErrors = (details as { fieldErrors?: unknown }).fieldErrors;
  return fieldErrors && typeof fieldErrors === 'object'
    ? (fieldErrors as Record<string, unknown>)
    : null;
}

/**
 * `@ts-rest/react-query` throws the whole typed response for any non-2xx status (see
 * `create-hooks.cjs.js`'s `apiFetcher`), so a query/mutation's `error` object carries `status`
 * directly — this is how the careers pages tell a 404 (not found) from a 409 (already applied /
 * already responded) apart, rather than treating every failure the same way.
 */
export function getErrorStatus(error: unknown): number | undefined {
  const status = (error as { status?: unknown } | undefined)?.status;
  return typeof status === 'number' ? status : undefined;
}
